import { useCallback, useContext, useLayoutEffect, useEffect, useState } from 'react';
import { Image, Pressable, ScrollView, StyleSheet } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { View } from '@/components/Themed';
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from '@/utilities/AppUtilities';
import { CustomText } from '@/components/CustomText';
import { Button, Hourglass } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { removeSearchResultByID, setData, appendSearchResults } from '@/state/user';
import { RelativePathString, router, useRouter, useLocalSearchParams } from 'expo-router';
import Footer from '@/components/Footer';
import { CommunityMembership, UNKNOWN_COMMUNITY_MEMBERSHIP } from "@/types/community_membership";
import * as AppConstants from '@/utilities/AppConstants';
import Profile from '@/components/Profile';
import { AppContext } from '@/components/AppContext';
import { useWindowDimensions } from 'react-native';
import { useNavigation } from "expo-router/react-navigation";
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  image: {
    width: 200,
    height: 200,
  },
  bottomLeft: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    backgroundColor: 'transparent',
  },
  bottomRight: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    backgroundColor: 'transparent',
  },
  imageWrapper: {
    backgroundColor: 'transparent',
  },
  wrapper: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  decisionImage: {
    width: AppConsts.PROFILE_DECISION_IMAGE_DIMENSION,
    height: AppConsts.PROFILE_DECISION_IMAGE_DIMENSION,
  },
  decisionWrapper: {
    flexDirection: 'row',
    justifyContent: AppUtilities.isWeb() ? 'center' : 'flex-end',
    paddingRight: AppUtilities.isWeb() ? 0 : 20,
    backgroundColor: 'transparent',
    width: '100%',
    gap: 40,
    height: AppConsts.PROFILE_DECISION_TOTAL_HEIGHT,
    paddingVertical: 10,
  },
  // Center:
  /*decisionWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    width: '100%',
    gap: 40,
    position: 'absolute',
    bottom: 0,
  },*/
  links: {
    backgroundColor: 'transparent',
    //paddingTop: 74,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  bottomView: {
    paddingBottom: 20,
    backgroundColor: 'transparent',
  },
});

export default function ShowProfile() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const { profile_membership_id, d : showDecisions } = useLocalSearchParams<{ profile_membership_id: string, d?: string }>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState<CommunityMembership>(UNKNOWN_COMMUNITY_MEMBERSHIP);
  const dispatch = useDispatch();
  const accumulatedSearchResults = useSelector((state: RootState) => state.user.accumulatedSearchResults);
  const navigation = useNavigation();
  const next_search_offset = useSelector((state: RootState) => state.user.next_search_offset);
  const search_string = useSelector((state: RootState) => state.user.search_string);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);

  useLayoutEffect(() => {
    if (AppUtils.isValidCommunityMembership(member) && member.profile) {
      navigation.setOptions({
        title: member.profile.name,
      });
    }
  }, [navigation, member]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      AppUtilities.info("Profile.loadInitialData started");
      const response = await AppUtilities.fetchAuthorized(
        "ShowProfile.loadInitialData",
        "/rabbl/api/profile",
        "POST",
        {
          domain: domain,
          id: profile_membership_id,
        }
      );
      AppUtilities.info("ShowProfile.loadInitialData finished:");
      AppUtilities.dir((response as any).body);
      const memberResult: CommunityMembership = ((response as any).body as any).community_membership;
      setMember(memberResult);
    } catch (e : any) {
      setError(AppUtilities.getErrors(e));
      AppUtils.handleExceptionUnlessParticularStatus(e, "ShowProfile.loadInitialData", 404);
    } finally {
      setLoading(false);
    }
  }, [domain, profile_membership_id]);

  const decide = useCallback(async (like: boolean, decisionType: string) => {
    if (communityMembership.hidden) {
      AppUtils.showErrorToast("Your profile is currently hidden so you can't decide on profiles.", () => router.navigate("/settings"), true, "Settings");
      return;
    }

    try {
      showSpinner();
      AppUtilities.info("Profile.loadInitialData started");
      const response = await AppUtilities.fetchAuthorized(
        "ShowProfile.decide",
        "/rabbl/api/decide",
        "POST",
        {
          domain: domain,
          id: profile_membership_id,
          like: like,
          decisionType: decisionType,
        }
      );
      AppUtilities.info("ShowProfile.decide finished:");
      AppUtilities.dir((response as any).body);

      let nextProfile = "-1";
      // Let's first figure out the next match we want to go to
      // First find the index of this current profile
      const currentProfileId = profile_membership_id;
      const currentProfileIndex = accumulatedSearchResults.findIndex((s) => s.uniqueid == currentProfileId);
      if (currentProfileIndex != -1) {
        // If it's not the last profile
        if (currentProfileIndex != accumulatedSearchResults.length - 1) {
          nextProfile = accumulatedSearchResults[currentProfileIndex + 1].uniqueid;
        } else if (currentProfileIndex >= 1) {
          nextProfile = accumulatedSearchResults[currentProfileIndex - 1].uniqueid;
        }
      }

      dispatch(removeSearchResultByID(currentProfileId));
      dispatch(setData(response));

      const new_match: boolean = ((response as any).body as any).new_match;
      let shouldPop = true;
      if (new_match) {
        AppUtils.showPressableToast(
          "You've got a match!",
          () => {
            router.navigate('/chats');
          },
          "Show",
        );
      }

      if (nextProfile == "-1") {
        // Might be more matches
        AppUtilities.debug("Checking for more matches");
        const response = await AppUtilities.fetchAuthorized(
          "ShowProfile.decide",
          "/rabbl/api/search",
          "POST",
          {
            domain: domain,
            offset: next_search_offset,
            search_string: search_string,
          }
        );
        dispatch(appendSearchResults(response));
        const potential_matches: CommunityMembership[] = ((response as any).body as any).potential_matches;
        AppUtilities.debug("Found " + potential_matches.length + " more matches, picking next one");
        if (potential_matches.length > 0) {
          nextProfile = potential_matches[0].uniqueid;
        }
      }

      if (nextProfile != "-1") {
        router.replace(("/profile/" + nextProfile) as RelativePathString);
      } else {
        if (shouldPop) {
          if (router.canGoBack()) {
            router.back();
          } else {
            router.navigate('/');
          }
        }
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => decide(like, decisionType), true);
      AppUtilities.handleException(e, "ShowProfile.decide");
    } finally {
      hideSpinner();
    }
  }, [domain, profile_membership_id, accumulatedSearchResults, next_search_offset, search_string, communityMembership]);

  const openmatch = useCallback(async () => {
    if (communityMembership.hidden) {
      AppUtils.showErrorToast("Your profile is currently hidden so you can't start new chats.", () => router.navigate("/settings"), true, "Settings");
      return;
    }

    try {
      showSpinner();
      const response = await AppUtilities.fetchAuthorized(
        "ShowProfile.openmatch",
        "/rabbl/api/openmatch",
        "POST",
        {
          domain: domain,
          id: profile_membership_id,
        }
      );
      AppUtilities.dir((response as any).body);
      const currentProfileId = profile_membership_id;
      const new_match_id: number = ((response as any).body as any).new_match_id;
      dispatch(removeSearchResultByID(currentProfileId));
      dispatch(setData(response));
      router.replace(("/chat/" + new_match_id) as RelativePathString);
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => openmatch(), true);
      AppUtilities.handleException(e, "ShowProfile.openmatch");
    } finally {
      hideSpinner();
    }
  }, [domain, profile_membership_id]);

  const report = useCallback(async () => {
    decide(false, "report");
  }, [domain, profile_membership_id]);

  const block = useCallback(async () => {
    decide(false, "block");
  }, [domain, profile_membership_id]);

  useEffect(() => {
    if (profile_membership_id) {
      loadInitialData();
    }
  }, [domain, profile_membership_id]);

  if (loading) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <Hourglass size={AppConstants.LARGE_HOURGLASS_SIZE} />
      </View>
    );
  }

  if (error.length > 0) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <CustomText>Failed to load</CustomText>
        <AppText>{error}</AppText>
        <Button onPress={loadInitialData}>Retry</Button>
      </View>
    );
  }

  return (
    <View style={styles.wrapper}>
      <BasicScrollView center={false}>
        <Meta title={member.profile.name} />
        <Profile
          member={member}
          showName={false}
          extraOffsetHeight={0}
          referenceDomain={domain}
        />
        {
          showDecisions != "n" && (
            <View style={styles.links}>
              <Pressable onPress={report}><CustomText type="link">report</CustomText></Pressable>
              <CustomText> | </CustomText>
              <Pressable onPress={block}><CustomText type="link">block</CustomText></Pressable>
            </View>
          )
        }
        <View style={styles.bottomView} />
      </BasicScrollView>
      {
        showDecisions != "n" && (
          <View style={styles.decisionWrapper}>
            {
              member.user_info.pref_allow_unmatched_messages &&
                <Pressable
                  style={({ pressed }) => [
                    styles.imageWrapper,
                    {
                      opacity: pressed ? 0.5 : 1,
                    },
                  ]}
                  onPress={openmatch}
                >
                  <Image style={styles.decisionImage} source={require('@/assets/images/PixelarticonsMessageText64x64.png')} />
                </Pressable>
            }
            <Pressable
              style={({ pressed }) => [
                styles.imageWrapper,
                {
                  opacity: pressed ? 0.5 : 1,
                },
              ]}
              onPress={() => decide(true, "normal")}
            >
              <Image style={styles.decisionImage} source={require('@/assets/images/heart64x64.png')} />
            </Pressable>
            <Pressable
              style={({ pressed }) => [
                styles.imageWrapper,
                {
                  opacity: pressed ? 0.5 : 1,
                },
              ]}
              onPress={() => decide(false, "normal")}
            >
              <Image style={styles.decisionImage} source={require('@/assets/images/PixelarticonsClose64x64-black.png')} />
            </Pressable>
          </View>
        )
      }
    </View>
  );
}
